import { SidebarInset, SidebarProvider } from '@repo/ui'
import { Outlet, createFileRoute, redirect, useLoaderData } from '@tanstack/react-router'
import type { Project } from '@/components/AppSidebar'
import { AppSidebar } from '@/components/AppSidebar'
import { getOrgUsers } from '@/api/org/users'
import { getProjects } from '@/api/project/list'

export const Route = createFileRoute('/_auth/$orgSlug')({
  beforeLoad: ({ context, params, location }) => {
    const userSlug = context.user.userOrgProfile?.org.slug
    if (userSlug && params.orgSlug !== userSlug) {
      throw redirect({ to: '/org-mismatch', search: { redirect: location.href } })
    }
  },
  loader: async ({ context }) => {
    const uid = context.user.userOrgProfile?.uid

    const [allProjects, orgUsers] = await Promise.all([getProjects(), getOrgUsers()])

    const myProjects: Array<Project> = allProjects
      .filter((p) => p.members.length === 1 && p.members[0]?.userId === uid)
      .map((p) => ({ id: p.projectId, name: p.title, lastUpdated: p.updatedOnTsMs }))

    const sharedProjects: Array<Project> = allProjects
      .filter((p) => p.members.length > 1)
      .map((p) => ({ id: p.projectId, name: p.title, lastUpdated: p.updatedOnTsMs }))

    return {
      allProjects,
      myProjects,
      sharedProjects,
      orgUsers
    }
  },
  component: OrgLayout
})

function OrgLayout() {
  const { orgSlug } = Route.useParams()
  const { organizations } = useLoaderData({ from: '/_auth' })
  const { myProjects, sharedProjects, orgUsers } = useLoaderData({ from: Route.id })
  const { userOrgProfile } = Route.useRouteContext().user

  return (
    <SidebarProvider>
      <AppSidebar
        organizations={organizations}
        myProjects={myProjects}
        sharedProjects={sharedProjects}
        currentOrgSlug={orgSlug}
        contentConnectorsEnabled={userOrgProfile?.enabled_features.content_connectors ?? false}
        globalContentEnabled={userOrgProfile?.enabled_features.global_content ?? false}
        orgUsers={orgUsers}
      />
      <SidebarInset>
        <Outlet />
      </SidebarInset>
    </SidebarProvider>
  )
}
